import { memo, useState, useCallback } from 'react';
import { Badge, Table, Spinner } from 'react-bootstrap';

import { organizationService } from '@/services';
import type {
  Organization,
  OrganizationFacility,
  OrganizationSortableField,
  OrganizationWithFacilities,
} from '@/types';
import { getConfidenceLevel, getMatchMethodDescription } from '@/types';

import styles from './OrganizationsTable.module.scss';

interface OrganizationsTableProps {
  readonly organizations: readonly Organization[];
  readonly sortField?: OrganizationSortableField;
  readonly sortOrder?: 'asc' | 'desc';
  readonly onSort?: (field: OrganizationSortableField) => void;
}

interface ColumnDef {
  readonly field: OrganizationSortableField;
  readonly label: string;
  readonly className?: string;
}

const COLUMNS: readonly ColumnDef[] = [
  { field: 'name', label: 'Organization', className: styles.nameCell },
  {
    field: 'facility_count',
    label: 'Locations',
    className: styles.countCell,
  },
  {
    field: 'avg_confidence',
    label: 'Confidence',
    className: styles.confidenceCell,
  },
];

function getConfidenceVariant(
  confidence: number | null
): 'success' | 'warning' | 'secondary' {
  const level = getConfidenceLevel(confidence);
  switch (level) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'secondary';
  }
}

const SortHeader = memo<{
  column: ColumnDef;
  sortField?: OrganizationSortableField;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: OrganizationSortableField) => void;
}>(function SortHeader({ column, sortField, sortOrder, onSort }) {
  const isActive = sortField === column.field;
  const isAsc = isActive && sortOrder === 'asc';
  const isDesc = isActive && sortOrder === 'desc';

  const handleClick = (): void => {
    onSort?.(column.field);
  };

  const classNames = [
    styles.sortableHeader,
    column.className,
    isActive ? styles.activeSort : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <th className={classNames} onClick={handleClick}>
      <div className={styles.headerContent}>
        <span>{column.label}</span>
        <span className={styles.sortIcon}>
          {isAsc && <i className="bi bi-sort-up" />}
          {isDesc && <i className="bi bi-sort-down" />}
          {!isActive && <i className="bi bi-chevron-expand" />}
        </span>
      </div>
    </th>
  );
});

const FacilityRow = memo<{ facility: OrganizationFacility }>(
  function FacilityRow({ facility }) {
    const address = [
      facility.address.street_1,
      facility.address.city,
      facility.address.province,
    ]
      .filter(Boolean)
      .join(', ');

    return (
      <div className={styles.facilityRow}>
        <div className={styles.facilityInfo}>
          <span className={styles.facilityName}>{facility.name}</span>
          <span className={styles.facilityAddress}>{address}</span>
        </div>
        <div className={styles.facilityMeta}>
          <Badge
            bg={getConfidenceVariant(facility.confidence)}
            className={styles.confidenceBadge}
            title={getMatchMethodDescription(facility.match_method)}
          >
            {facility.confidence}%
          </Badge>
        </div>
      </div>
    );
  }
);

const OrganizationRow = memo<{
  organization: Organization;
  sortField?: OrganizationSortableField;
}>(function OrganizationRow({ organization }) {
  const [expanded, setExpanded] = useState(false);
  const [facilities, setFacilities] = useState<
    readonly OrganizationFacility[] | null
  >(null);
  const [loading, setLoading] = useState(false);

  const handleToggle = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    if (!facilities) {
      setLoading(true);
      try {
        const data: OrganizationWithFacilities =
          await organizationService.fetchOrganizationById(organization.id);
        setFacilities(data.facilities);
      } catch (err) {
        console.error('Error fetching organization facilities:', err);
      } finally {
        setLoading(false);
      }
    }

    setExpanded(true);
  }, [expanded, facilities, organization.id]);

  return (
    <>
      <tr className={styles.clickableRow} onClick={handleToggle}>
        <td className={styles.nameCell}>
          <div className={styles.orgNameWrapper}>
            <i
              className={`bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'} ${styles.expandIcon}`}
            />
            <span className={styles.orgName}>{organization.name}</span>
          </div>
        </td>
        <td className={styles.countCell}>
          <Badge bg="primary" className={styles.countBadge}>
            {organization.facility_count}
          </Badge>
        </td>
        <td className={styles.confidenceCell}>
          <Badge
            bg={getConfidenceVariant(organization.avg_confidence)}
            className={styles.confidenceBadge}
          >
            {organization.avg_confidence !== null
              ? `${Math.round(organization.avg_confidence)}%`
              : '-'}
          </Badge>
        </td>
      </tr>
      {expanded && (
        <tr className={styles.expandedRow}>
          <td colSpan={3} className={styles.facilitiesCell}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <Spinner size="sm" animation="border" />
                <span>Loading facilities...</span>
              </div>
            ) : facilities && facilities.length > 0 ? (
              <div className={styles.facilitiesList}>
                {facilities.map((facility) => (
                  <FacilityRow key={facility.id} facility={facility} />
                ))}
              </div>
            ) : (
              <div className={styles.noFacilities}>No facilities found</div>
            )}
          </td>
        </tr>
      )}
    </>
  );
});

export const OrganizationsTable = memo<OrganizationsTableProps>(
  function OrganizationsTable({
    organizations,
    sortField,
    sortOrder,
    onSort,
  }) {
    if (organizations.length === 0) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <i className="bi bi-building" />
          </div>
          <p className={styles.emptyText}>
            No organizations found matching your criteria.
          </p>
        </div>
      );
    }

    return (
      <div className={styles.tableContainer}>
        <Table hover className={styles.organizationsTable}>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <SortHeader
                  key={column.field}
                  column={column}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <OrganizationRow
                key={org.id}
                organization={org}
                sortField={sortField}
              />
            ))}
          </tbody>
        </Table>
      </div>
    );
  }
);
